import axios from 'axios';

/**
 * Whiteboard Service
 *
 * Handles all whiteboard-related API requests to the backend.
 */

// API Base URL from environment variable or default to localhost
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5001/api';

// Whiteboard endpoints
const WHITEBOARD_ENDPOINTS = {
  get: (projectId: string) => `${API_BASE_URL}/projects/${projectId}/whiteboard`,
  save: (projectId: string) => `${API_BASE_URL}/projects/${projectId}/whiteboard`,
  clear: (projectId: string) => `${API_BASE_URL}/projects/${projectId}/whiteboard`
};

/**
 * Whiteboard data interface
 */
export interface WhiteboardData {
  id: string;
  project_id: string;
  canvas_data: string | null;
  version: number;
  updated_at: string | null;
}

/**
 * Get whiteboard for a project (creates if doesn't exist)
 * @param projectId - The project ID
 * @param token - JWT auth token
 * @returns Whiteboard data
 */
export async function getWhiteboard(
  projectId: string,
  token: string
): Promise<WhiteboardData> {
  const response = await axios.get(WHITEBOARD_ENDPOINTS.get(projectId), {
    headers: { Authorization: `Bearer ${token}` }
  });

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to get whiteboard');
  }

  return response.data.data;
}

/**
 * Save whiteboard canvas data
 * @param projectId - The project ID
 * @param canvasData - The canvas data to save (stringified JSON)
 * @param token - JWT auth token
 * @returns Updated whiteboard metadata
 */
export async function saveWhiteboard(
  projectId: string,
  canvasData: string,
  token: string
): Promise<{ version: number; updated_at: string }> {
  const response = await axios.put(
    WHITEBOARD_ENDPOINTS.save(projectId),
    { canvas_data: canvasData },
    { headers: { Authorization: `Bearer ${token}` } }
  );

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to save whiteboard');
  }

  return response.data.data;
}

/**
 * Clear whiteboard canvas data
 * @param projectId - The project ID
 * @param token - JWT auth token
 * @returns Success boolean
 */
export async function clearWhiteboard(
  projectId: string,
  token: string
): Promise<boolean> {
  const response = await axios.delete(WHITEBOARD_ENDPOINTS.clear(projectId), {
    headers: { Authorization: `Bearer ${token}` }
  });

  return response.data.success;
}
